import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  TextField,
  InputAdornment,
  Pagination,
  Alert,
  styled,
  useTheme,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Grid,
  Divider,
  Card,
  CardContent,
  Collapse,
  FormHelperText,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  ArrowDropDown,
  Close as CloseIcon,
  FilterList as FilterListIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import useFetchObjects from '../../api/useFetchObjects';
import useFetchObject from '../../api/useFetchObject';
import useDelete from '../../api/useDelete';
import useAdd from '../../api/useAdd';
import useUpdate from '../../api/useUpdate';
import { useAuth } from '../../context/AuthContext';

// RTL-aware TextField component
const RTLTextField = styled(TextField)(({ theme, isRtl }) => ({
  ...(isRtl && {
    '& .MuiInputBase-input': {
      textAlign: 'right',
      direction: 'rtl',
      padding: '10px 10px 16px',
    },
    '& .MuiInputLabel-root': {
      right: 25,
      left: 'auto',
      transformOrigin: 'right',
      marginRight: '5px',
    },
    '& .MuiOutlinedInput-root': {
      paddingLeft: 14,
      paddingRight: 0,
    },
    '& .MuiInputAdornment-root': {
      marginRight: 8,
      marginLeft: 0,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      textAlign: 'right',
    },
    '& .MuiFormHelperText-root': {
      textAlign: 'right',
    },
  }),
}));

// RTL-aware TableCell component with bold header option
const RTLTableCell = styled(TableCell)(({ theme, isRtl, align, isHeader }) => ({
  ...(isRtl && {
    textAlign: align || 'right',
    direction: 'rtl',
  }),
  ...(isHeader && {
    fontWeight: 'bold',
  }),
}));

// RTL-aware FormControl with improved dropdown styling
const RTLFormControl = styled(FormControl)(({ theme, isRtl }) => ({
  ...(isRtl && {
    '& .MuiInputLabel-root': {
      right: 25,
      left: 'auto',
      transformOrigin: 'right',
      marginRight: '5px',
    },
    '& .MuiInputBase-input': {
      textAlign: 'right',
      direction: 'rtl',
    },
    '& .MuiSelect-select': {
      paddingRight: '32px',
      textAlign: 'right',
      paddingLeft: '32px',
    },
    '& .MuiSelect-icon': {
      right: 'auto',
      left: 7,
      pointerEvents: 'none',
      color: theme.palette.action.active,
    },
    '& .MuiFormHelperText-root': {
      textAlign: 'right',
    },
  }),
}));

// Custom styled button for better RTL support
const RTLButton = styled(Button)(({ theme, isRtl }) => ({
  ...(isRtl && {
    flexDirection: 'row-reverse',
  }),
}));

// RTL Dialog Title
const RTLDialogTitle = styled(DialogTitle)(({ theme, isRtl }) => ({
  ...(isRtl && {
    textAlign: 'right',
    direction: 'rtl',
  }),
}));

// RTL Dialog Content
const RTLDialogContent = styled(DialogContent)(({ theme, isRtl }) => ({
  ...(isRtl && {
    textAlign: 'right',
    direction: 'rtl',
  }),
}));

// RTL Dialog Actions
const RTLDialogActions = styled(DialogActions)(({ theme, isRtl }) => ({
  ...(isRtl && {
    flexDirection: 'row-reverse',
  }),
}));

const DeputyMinistryList = () => {
  const { t, i18n } = useTranslation(['deputy-ministries', 'common', 'validation']);
  const { user } = useAuth();
  const token = user?.token;
  const isRtl = i18n.language === 'dari' || i18n.language === 'ps';
  const theme = useTheme();
  
  // State for filtering and pagination
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [ministryFilter, setMinistryFilter] = useState('');

  // State for dialog management
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedDeputyMinistryId, setSelectedDeputyMinistryId] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    ministry_id: '',
  });

  // Validation state
  const [errors, setErrors] = useState({});

  // Query parameters for API call
  const params = {
    page,
    limit,
    search,
    ministryId: ministryFilter || undefined
  };
  
  // Fetch deputy ministries
  const { 
    data, 
    isLoading, 
    isError, 
    error,
    refetch 
  } = useFetchObjects('deputy-ministries', params, token);
  
  // Fetch users for deputy minister selection
  const { 
    data: usersData, 
    isLoading: isLoadingUsers 
  } = useFetchObjects('users', { role: 'minister' }, token);
  
  const users = usersData?.data || [];

  // Fetch ministries for selection
  const { 
    data: ministriesData, 
    isLoading: isLoadingMinistries 
  } = useFetchObjects('ministries', {}, token);
  
  // Ensure ministries array is correctly extracted regardless of backend envelope
  const ministries = Array.isArray(ministriesData) ? ministriesData : (ministriesData?.data || []);

  // Fetch deputy ministry data for edit
  const { 
    data: deputyMinistryData, 
    isLoading: isLoadingDeputyMinistry,
  } = useFetchObject('deputy-ministries', selectedDeputyMinistryId ? `deputy-ministries/${selectedDeputyMinistryId}` : null, selectedDeputyMinistryId, token);

  // Delete functionality
  const { handleDelete, ConfirmDialog } = useDelete('deputy-ministries', token);

  // Create functionality
  const { 
    handleAdd, 
    loading: isCreating, 
    success: createSuccess 
  } = useAdd(
    'deputy-ministries',
    token,
    '/deputy-ministries',
    t('deputy-ministries:create_success'),
    t('deputy-ministries:create_error')
  );

  // Update functionality
  const { 
    handleUpdate, 
    loading: isUpdating, 
    success: updateSuccess 
  } = useUpdate(
    'deputy-ministries',
    token,
    '/deputy-ministries',
    t('deputy-ministries:update_success'),
    t('deputy-ministries:update_error')
  );

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearch(e.target.value);
  };

  // Handle search form submission
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setPage(1);
    refetch();
  };

  // Handle ministry filter change
  const handleMinistryChange = (e) => {
    setMinistryFilter(e.target.value);
    setPage(1); // Reset to first page when filter changes
  };

  // Handle page change
  const handlePageChange = (event, value) => {
    setPage(value);
  };

  // Handle page size change
  const handleLimitChange = (e) => {
    setLimit(Number(e.target.value));
    setPage(1); // Reset to first page when limit changes
  };

  // Open create dialog
  const handleOpenCreateDialog = () => {
    setFormData({
      name: '',
      description: '',
      ministry_id: '',
    });
    setErrors({});
    setCreateDialogOpen(true);
  };

  // Close create dialog
  const handleCloseCreateDialog = () => {
    setCreateDialogOpen(false);
  };

  // Open edit dialog
  const handleOpenEditDialog = (id) => {
    setSelectedDeputyMinistryId(id);
    setEditDialogOpen(true);
  };

  // Close edit dialog
  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setSelectedDeputyMinistryId(null);
  };

  // Handle input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    
    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: null,
      }));
    }
  };

  // Set form data when deputy ministry data is loaded (for edit)
  React.useEffect(() => {
    if (deputyMinistryData && editDialogOpen) {
      const ministryData = deputyMinistryData.data || deputyMinistryData;
      
      setFormData({
        name: ministryData.name || '',
        description: ministryData.description || '',
        ministry_id: ministryData.ministry_id || '',
      });
    }
  }, [deputyMinistryData, editDialogOpen]);

  // Reset form and close dialog when operation is successful
  React.useEffect(() => {
    if (createSuccess) {
      handleCloseCreateDialog();
      refetch();
    }
    
    if (updateSuccess) {
      handleCloseEditDialog();
      refetch();
    }
  }, [createSuccess, updateSuccess]);

  // Validate form
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = t('validation:required');
    }
    
    if (!formData.ministry_id) {
      newErrors.ministry_id = t('validation:required');
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle create form submission
  const handleCreateSubmit = (e) => {
    e.preventDefault();
    
    // Validate form
    if (!validateForm()) {
      return;
    }
    
    // Create a copy of the form data
    const submissionData = { ...formData };
    
    // Convert empty string to null for foreign key fields
    
    
    if (submissionData.ministry_id === '') {
      submissionData.ministry_id = null;
    }
    
    // Submit form data
    handleAdd(submissionData);
  };

  // Handle edit form submission
  const handleEditSubmit = (e) => {
    e.preventDefault();
    
    // Validate form
    if (!validateForm()) {
      return;
    }
    
    // Create a copy of the form data
    const submissionData = { ...formData };
    
    // Convert empty string to null for foreign key fields
   
    if (submissionData.ministry_id === '') {
      submissionData.ministry_id = null;
    }
    
    // Submit form data
    handleUpdate(selectedDeputyMinistryId, submissionData);
  };

  // Calculate total pages
  const totalPages = data?.totalPages || data?.pagination?.totalPages || 1;

  // Deputy ministries list (avoid naming collision with filter array)
  const deputyMinistriesList = data?.deputyMinistries || data?.data || (Array.isArray(data) ? data : []);

  // Total items for range display
  const totalItems = data?.count ?? data?.total ?? data?.pagination?.total ?? deputyMinistriesList.length;

  return (
    <Box dir={isRtl ? 'rtl' : 'ltr'} sx={{ width: '100%' }}>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        mb: 3,
        flexDirection: isRtl ? 'row-reverse' : 'row',
        width: '100%'
      }}>
        <RTLButton
          variant="contained"
          startIcon={!isRtl && <AddIcon />}
          endIcon={isRtl && <AddIcon />}
          onClick={handleOpenCreateDialog}
          isRtl={isRtl}
          sx={{ 
            whiteSpace: 'nowrap',
            borderRadius: 2,
            px: 2,
            py: 0.75,
            textTransform: 'none',
            fontWeight: 500,
            boxShadow: 2,
            '&:hover': {
              boxShadow: 4
            }
          }}
        >
          {t('create_new')}
        </RTLButton>
        
        <Typography 
          variant="h4" 
          component="h1" 
          sx={{ 
            textAlign: isRtl ? 'right' : 'left',
            fontWeight: 'bold',
          }}
        >
          {t('list_title')}
        </Typography>
      </Box>
      
      {/* Search and filters */}
      <Box sx={{ 
        display: 'flex', 
        mb: 3, 
        gap: 2,
        flexDirection: isRtl ? 'row-reverse' : 'row',
        width: '100%'
      }}>
        <RTLTextField
          variant="outlined"
          size="small"
          placeholder={t('common:search')}
          value={search}
          onChange={handleSearchChange}
          sx={{ 
            flexGrow: 1,
            '& .MuiOutlinedInput-root': {
              paddingRight: isRtl ? '0px' : '0',
              paddingLeft: isRtl ? '0' : '0px',
            },
            '& .MuiOutlinedInput-input': {
              padding: '10px 14px',
            }
          }}
          isRtl={isRtl}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            dir: isRtl ? 'rtl' : 'ltr',
          }}
        />
        
        <RTLButton 
          variant="contained" 
          onClick={handleSearchSubmit}
          isRtl={isRtl}
        >
          {t('common:search')}
        </RTLButton>
        
        <RTLButton 
          variant="outlined" 
          onClick={() => {
            setSearch('');
            setMinistryFilter('');
            refetch();
          }}
          isRtl={isRtl}
        >
          {t('common:reset')}
        </RTLButton>
        
        <RTLButton 
          variant="outlined" 
          startIcon={!isRtl && <RefreshIcon />}
          endIcon={isRtl && <RefreshIcon />}
          onClick={refetch}
          isRtl={isRtl}
        >
          {t('common:refresh')}
        </RTLButton>
        
        <RTLButton
          variant="text"
          color="primary"
          onClick={() => setShowFilters(!showFilters)}
          startIcon={!isRtl && (showFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />)}
          endIcon={isRtl && (showFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />)}
          isRtl={isRtl}
        >
          <FilterListIcon fontSize="small" sx={{ mr: 0.5 }} />
          {t('common:filter')}
        </RTLButton>
      </Box>
      
      <Collapse in={showFilters}>
        <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: isRtl ? 'row-reverse' : 'row', 
            gap: 2, 
            flexWrap: 'wrap' 
          }}>
            <RTLFormControl 
              sx={{ minWidth: 200, flexGrow: 1 }} 
              size="small"
              isRtl={isRtl}
            >
              <InputLabel id="ministry-filter-label">{t('ministry')}</InputLabel>
              <Select
                labelId="ministry-filter-label"
                value={ministryFilter}
                onChange={handleMinistryChange}
                label={t('ministry')}
                MenuProps={{
                  anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: isRtl ? 'right' : 'left',
                  },
                  transformOrigin: {
                    vertical: 'top',
                    horizontal: isRtl ? 'right' : 'left',
                  },
                  PaperProps: {
                    style: {
                      direction: isRtl ? 'rtl' : 'ltr',
                    },
                  },
                }}
                IconComponent={(props) => (
                  <span
                    {...props}
                    style={{
                      position: 'absolute',
                      right: isRtl ? 'auto' : '7px',
                      left: isRtl ? '7px' : 'auto',
                      pointerEvents: 'none',
                    }}
                  >
                    <ArrowDropDown />
                  </span>
                )}
              >
                <MenuItem value="">
                  <em>{t('common:all')}</em>
                </MenuItem>
                {isLoadingMinistries ? (
                  <MenuItem disabled>
                    <CircularProgress size={20} /> {t('common:loading')}
                  </MenuItem>
                ) : (
                  ministries.map(ministry => (
                    <MenuItem key={ministry.id} value={ministry.id}>
                      {ministry.name}
                    </MenuItem>
                  ))
                )}
              </Select>
            </RTLFormControl>
          </Box>
        </Paper>
      </Collapse>

      {/* Error alert */}
      {isError && (
        <Alert severity="error" sx={{ mb: 3, textAlign: isRtl ? 'right' : 'left' }}>
          {error?.message || t('error_occurred')}
        </Alert>
      )}

      {/* Table */}
      <Paper sx={{ width: '100%', overflow: 'hidden', mb: 3 }}>
        <TableContainer>
          <Table>
            <TableHead sx={{ 
              bgcolor: theme.palette.grey[100],
              '& .MuiTableRow-root': {
                borderBottom: `2px solid ${theme.palette.grey[300]}`,
              }
            }}>
              <TableRow>
                <RTLTableCell 
                  isRtl={isRtl} 
                  isHeader
                  align={isRtl ? "right" : "left"}
                  sx={{ 
                    fontSize: '1rem',
                    fontWeight: 600,
                    color: theme.palette.text.primary,
                    textAlign: isRtl ? 'right' : 'left'
                  }}
                >
                   {t('id')}
                </RTLTableCell>
                <RTLTableCell 
                  isRtl={isRtl} 
                  isHeader
                  sx={{ 
                    fontSize: '1rem',
                    fontWeight: 600,
                    color: theme.palette.text.primary
                  }}
                >
                  {t('name')}
                </RTLTableCell>
                <RTLTableCell 
                  isRtl={isRtl} 
                  isHeader
                  sx={{ 
                    fontSize: '1rem',
                    fontWeight: 600,
                    color: theme.palette.text.primary
                  }}
                >
                  {t('description')}
                </RTLTableCell>
                <RTLTableCell 
                  isRtl={isRtl} 
                  isHeader
                  sx={{ 
                    fontSize: '1rem',
                    fontWeight: 600,
                    color: theme.palette.text.primary
                  }}
                >
                  {t('ministry')}
                </RTLTableCell>
                <RTLTableCell 
                  isRtl={isRtl} 
                  isHeader
                  sx={{ 
                    fontSize: '1rem',
                    fontWeight: 600,
                    color: theme.palette.text.primary
                  }}
                >
                  {t('created_at')}
                </RTLTableCell>
                <RTLTableCell 
                  isRtl={isRtl} 
                  align={isRtl ? "right" : "right"} 
                  isHeader
                  sx={{ 
                    fontSize: '1rem',
                    fontWeight: 600,
                    color: theme.palette.text.primary,
                    textAlign: isRtl ? 'right' : 'right',
                  }}
                >
                  {t('common:actions')}
                </RTLTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <RTLTableCell colSpan={6} align="center" sx={{ py: 3 }} isRtl={isRtl}>
                    <CircularProgress size={30} />
                  </RTLTableCell>
                </TableRow>
              ) : deputyMinistriesList.length === 0 ? (
                <TableRow>
                  <RTLTableCell colSpan={6} align="center" sx={{ py: 3 }} isRtl={isRtl}>
                    {t('common:no_records')}
                  </RTLTableCell>
                </TableRow>
              ) : (
                deputyMinistriesList.map((deputyMinistry, index) => (
                  <TableRow 
                    key={deputyMinistry.id} 
                    hover
                    sx={{ 
                      '&:hover': { backgroundColor: 'grey.50' },
                      '& td': { 
                        py: 1.5, // Increased vertical padding for better spacing
                      } 
                    }}
                  >
                    <RTLTableCell isRtl={isRtl} align={isRtl ? "right" : "center"}>
                      <Typography variant="body2" sx={{ 
                        fontWeight: 500,
                        textAlign: isRtl ? 'right' : 'center' 
                      }}>
                        {(page - 1) * limit + index + 1}
                      </Typography>
                    </RTLTableCell>
                    <RTLTableCell isRtl={isRtl} sx={{ fontWeight: 500 }}>
                      {deputyMinistry.name}
                    </RTLTableCell>
                    <RTLTableCell isRtl={isRtl}>
                      {deputyMinistry.description || '-'}
                    </RTLTableCell>
                    <RTLTableCell isRtl={isRtl}>
                      {deputyMinistry.ministry?.name || t('not_assigned')}
                    </RTLTableCell>
                    <RTLTableCell isRtl={isRtl}>
                      {new Date(deputyMinistry.created_at).toLocaleDateString()}
                    </RTLTableCell>
                    <RTLTableCell isRtl={isRtl} align={isRtl ? "left" : "right"}>
                      <Box sx={{ display: 'flex', justifyContent: isRtl ? 'flex-start' : 'flex-end', gap: 1 }}>
                        <Tooltip title={t('common:edit')} placement={isRtl ? "right" : "left"}>
                          <IconButton
                            onClick={() => handleOpenEditDialog(deputyMinistry.id)}
                            size="small"
                            color="primary"
                            sx={{
                              '&:hover': {
                                backgroundColor: 'rgba(25, 118, 210, 0.08)', // Consistent primary light hover color
                              }
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t('common:delete')} placement={isRtl ? "right" : "left"}>
                          <IconButton 
                            onClick={() => handleDelete(deputyMinistry.id)}
                            size="small"
                            color="error"
                            sx={{
                              '&:hover': {
                                backgroundColor: 'rgba(211, 47, 47, 0.08)', // Consistent error light hover color
                              }
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </RTLTableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        {/* Pagination */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          p: 2,
          flexDirection: isRtl ? 'row-reverse' : 'row' 
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography 
              variant="body2" 
              color="text.secondary"
              sx={{ 
                mr: isRtl ? 0 : 2, 
                ml: isRtl ? 2 : 0,
                whiteSpace: 'nowrap'
              }}
            >
              {t('common:rows_per_page')}:
            </Typography>
            <Select
              value={limit}
              onChange={handleLimitChange}
              size="small"
              sx={{ 
                minWidth: 70,
                '& .MuiSelect-select': {
                  px: 2
                },
                ...(isRtl && {
                  '& .MuiSelect-select': {
                    paddingRight: '32px',
                    textAlign: 'right',
                    paddingLeft: '14px',
                  },
                  '& .MuiSelect-icon': {
                    right: 7,
                    left: 'auto',
                  },
                }),
              }}
              MenuProps={{
                disableScrollLock: true,
                anchorOrigin: {
                  vertical: 'top',
                  horizontal: isRtl ? 'right' : 'left',
                },
                transformOrigin: {
                  vertical: 'top',
                  horizontal: isRtl ? 'right' : 'left',
                },
              }}
            >
              <MenuItem value={5}>5</MenuItem>
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={25}>25</MenuItem>
              <MenuItem value={50}>50</MenuItem>
            </Select>
          </Box>
          
          {/* Make sure we always show pagination even if total is just 1 page */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
              {t('common:showing')} {(page - 1) * limit + 1}-
              {Math.min(page * limit, totalItems)} {t('common:of')} {totalItems}
            </Typography>
            <Pagination
              count={Math.max(totalPages, 1)}  
              page={page}
              onChange={handlePageChange}
              color="primary"
              dir={isRtl ? 'rtl' : 'ltr'}
              shape="rounded"
              size="small"
              showFirstButton
              showLastButton
            />
          </Box>
        </Box>
      </Paper>
      
      {/* Create Dialog */}
      <Dialog 
        open={createDialogOpen} 
        onClose={handleCloseCreateDialog}
        fullWidth
        maxWidth="sm"
        dir={isRtl ? 'rtl' : 'ltr'}
        PaperProps={{
          sx: {
            borderRadius: '8px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
            maxWidth: '600px',
            width: '100%',
            mx: 'auto',
          }
        }}
      >
        <RTLDialogTitle isRtl={isRtl} sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          fontWeight: 'bold',
          pb: 1,
          pt: 2,
          height: '60px',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}>
          <Typography variant="h5">{t('create_title')}</Typography>
          <IconButton onClick={handleCloseCreateDialog} size="small">
            <CloseIcon />
          </IconButton>
        </RTLDialogTitle>
        
        <RTLDialogContent isRtl={isRtl} sx={{ pt: 3 }}>
          <Box component="form" id="create-form" onSubmit={handleCreateSubmit}>
            <RTLTextField
              fullWidth
              name="name"
              label={t('name')}
              value={formData.name}
              onChange={handleInputChange}
              error={!!errors.name}
              helperText={errors.name}
              required
              margin="normal"
              isRtl={isRtl}
              InputProps={{
                dir: isRtl ? 'rtl' : 'ltr',
              }}
              FormHelperTextProps={{
                style: { textAlign: isRtl ? 'right' : 'left' }
              }}
             
            />
            
            <RTLTextField
              fullWidth
              name="description"
              label={t('description')}
              value={formData.description || ''}
              onChange={handleInputChange}
              multiline
              rows={3}
              margin="normal"
              isRtl={isRtl}
              InputProps={{
                dir: isRtl ? 'rtl' : 'ltr',
              }}
              sx={{
                mt: 1, 
                mb: 2
              }}
            />
            
            <RTLTextField
              select
              fullWidth
              name="ministry_id"
              label={t('ministry')}
              value={formData.ministry_id || ''}
              onChange={handleInputChange}
              margin="normal"
              isRtl={isRtl}
              InputProps={{
                dir: isRtl ? 'rtl' : 'ltr',
              }}
              sx={{
                mt: 1, 
                mb: 2
              }}
              error={!!errors.ministry_id}
              helperText={errors.ministry_id}
              required
              SelectProps={{
                disableScrollLock: true,
                anchorOrigin: {
                  vertical: 'bottom',
                  horizontal: isRtl ? 'right' : 'left',
                },
                transformOrigin: {
                  vertical: 'top',
                  horizontal: isRtl ? 'right' : 'left',
                },
                PaperProps: {
                  style: {
                    direction: isRtl ? 'rtl' : 'ltr',
                  },
                },
                IconComponent: (props) => (
                  <span
                    {...props}
                    style={{
                      position: 'absolute',
                      right: isRtl ? 'auto' : '7px',
                      left: isRtl ? '7px' : 'auto',
                      pointerEvents: 'none',
                    }}
                  >
                    <ArrowDropDown />
                  </span>
                ),
                sx: {
                  ...(isRtl && {
                    paddingRight: '14px',
                    paddingLeft: '14px',
                    '& .MuiSelect-select': {
                      paddingRight: '14px',
                      paddingLeft: '32px',
                    },
                  }),
                },
              }}
            >
              <MenuItem value="">
                <em>{t('none')}</em>
              </MenuItem>
              {isLoadingMinistries ? (
                <MenuItem disabled>
                  <CircularProgress size={20} /> {t('common:loading')}
                </MenuItem>
              ) : (
                ministries.map((ministry) => (
                  <MenuItem key={ministry.id} value={ministry.id}>
                    {ministry.name}
                  </MenuItem>
                ))
              )}
            </RTLTextField>
          </Box>
        </RTLDialogContent>
        
        <RTLDialogActions isRtl={isRtl} sx={{ 
          px: 3, 
          pb: 3, 
          gap: 1,
          borderTop: '1px solid',
          borderColor: 'divider',
          pt: 2
        }}>
          <RTLButton 
            variant="outlined"
            onClick={handleCloseCreateDialog}
            isRtl={isRtl}
          >
            {t('common:cancel')}
          </RTLButton>
          <RTLButton 
            variant="contained"
            type="submit"
            form="create-form"
            disabled={isCreating}
            isRtl={isRtl}
          >
            {isCreating ? (
              <CircularProgress size={24} />
            ) : (
              t('common:save')
            )}
          </RTLButton>
        </RTLDialogActions>
      </Dialog>
      
      {/* Edit Dialog */}
      <Dialog 
        open={editDialogOpen} 
        onClose={handleCloseEditDialog}
        fullWidth
        maxWidth="sm"
        dir={isRtl ? 'rtl' : 'ltr'}
        PaperProps={{
          sx: {
            borderRadius: '8px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
            maxWidth: '600px',
            width: '100%',
            mx: 'auto',
          }
        }}
      >
        <RTLDialogTitle isRtl={isRtl} sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          fontWeight: 'bold',
          pb: 1,
          pt: 2,
          height: '60px',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}>
          <Typography variant="h5">{t('edit_title')}</Typography>
          <IconButton onClick={handleCloseEditDialog} size="small">
            <CloseIcon />
          </IconButton>
        </RTLDialogTitle>
        
        <RTLDialogContent isRtl={isRtl} sx={{ pt: 3 }}>
          {isLoadingDeputyMinistry ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box component="form" id="edit-form" onSubmit={handleEditSubmit}>
              <RTLTextField
                fullWidth
                name="name"
                label={t('name')}
                value={formData.name}
                onChange={handleInputChange}
                error={!!errors.name}
                helperText={errors.name}
                required
                margin="normal"
                isRtl={isRtl}
                InputProps={{
                  dir: isRtl ? 'rtl' : 'ltr',
                }}
                FormHelperTextProps={{
                  style: { textAlign: isRtl ? 'right' : 'left' }
                }}
                sx={{
                  mt: 1, 
                  mb: 2
                }}
              />
              
              <RTLTextField
                fullWidth
                name="description"
                label={t('description')}
                value={formData.description || ''}
                onChange={handleInputChange}
                multiline
                rows={3}
                margin="normal"
                isRtl={isRtl}
                InputProps={{
                  dir: isRtl ? 'rtl' : 'ltr',
                }}
                
              />
              
              <RTLTextField
                select
                fullWidth
                name="ministry_id"
                label={t('ministry')}
                value={formData.ministry_id || ''}
                onChange={handleInputChange}
                margin="normal"
                isRtl={isRtl}
                InputProps={{
                  dir: isRtl ? 'rtl' : 'ltr',
                }}
                sx={{
                  mt: 1, 
                  mb: 2
                }}
                error={!!errors.ministry_id}
                helperText={errors.ministry_id}
                required
                SelectProps={{
                  disableScrollLock: true,
                  anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: isRtl ? 'right' : 'left',
                  },
                  transformOrigin: {
                    vertical: 'top',
                    horizontal: isRtl ? 'right' : 'left',
                  },
                  PaperProps: {
                    style: {
                      direction: isRtl ? 'rtl' : 'ltr',
                    },
                  },
                  IconComponent: (props) => (
                    <span
                      {...props}
                      style={{
                        position: 'absolute',
                        right: isRtl ? 'auto' : '7px',
                        left: isRtl ? '7px' : 'auto',
                        pointerEvents: 'none',
                      }}
                    >
                      <ArrowDropDown />
                    </span>
                  ),
                  sx: {
                    ...(isRtl && {
                      paddingRight: '14px',
                      paddingLeft: '14px',
                      '& .MuiSelect-select': {
                        paddingRight: '14px',
                        paddingLeft: '32px',
                      },
                    }),
                  },
                }}
              >
                <MenuItem value="">
                  <em>{t('none')}</em>
                </MenuItem>
                {isLoadingMinistries ? (
                  <MenuItem disabled>
                    <CircularProgress size={20} /> {t('common:loading')}
                  </MenuItem>
                ) : (
                  ministries.map((ministry) => (
                    <MenuItem key={ministry.id} value={ministry.id}>
                      {ministry.name}
                    </MenuItem>
                  ))
                )}
              </RTLTextField>
            </Box>
          )}
        </RTLDialogContent>
        
        <RTLDialogActions isRtl={isRtl} sx={{ 
          px: 3, 
          pb: 3, 
          gap: 1,
          borderTop: '1px solid',
          borderColor: 'divider',
          pt: 2
        }}>
          <RTLButton 
            variant="outlined"
            onClick={handleCloseEditDialog}
            isRtl={isRtl}
          >
            {t('common:cancel')}
          </RTLButton>
          <RTLButton 
            variant="contained"
            type="submit"
            form="edit-form"
            disabled={isUpdating || isLoadingDeputyMinistry}
            isRtl={isRtl}
          >
            {isUpdating ? (
              <CircularProgress size={24} />
            ) : (
              t('common:save')
            )}
          </RTLButton>
        </RTLDialogActions>
      </Dialog>
      
      {/* Delete confirmation dialog */}
      <ConfirmDialog message={t('delete_confirm_text')} />
    </Box>
  );
};

export default DeputyMinistryList; 